import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "motion/react";
import {
  Zap,
  TrendingUp,
  Bot,
  Sparkles,
  Image as ImageIcon,
  FileText,
  Film,
  Wallet,
} from "lucide-react";
import { useCompanyId } from "@/lib/CompanyContext";
import { costsApi, type CostReportPeriod, type CostReport } from "@/api/costs";
import { companiesApi } from "@/api/companies";

/**
 * Converte valor BRL pra ND Credits (1 R$ = 100 créditos) e formata.
 * Página /consumo é view de uso da plataforma — mostra créditos.
 */
function fmtCredits(brl: number): string {
  const credits = Math.round(brl * 100);
  return `${credits.toLocaleString("pt-BR")} créditos`;
}

function fmtPct(v: number): string {
  return `${v.toFixed(1)}%`;
}

function Card({
  children,
  style,
  ...rest
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      {...rest}
      style={{
        background: "var(--wf-paper)",
        border: "1px solid var(--wf-ink-5)",
        borderRadius: 14,
        padding: 18,
        boxShadow: "0 1px 0 rgba(0,0,0,0.02)",
        ...style,
      }}
    >
      {children}
    </div>
  );
}

function CardLabel({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontSize: 11,
        fontWeight: 600,
        color: "var(--wf-ink-3)",
        textTransform: "uppercase",
        letterSpacing: "0.06em",
        marginBottom: 10,
      }}
    >
      {children}
    </div>
  );
}

function KpiCard({
  label,
  value,
  subtitle,
  accent,
  icon: IconCmp,
  hint,
}: {
  label: string;
  value: string;
  subtitle?: string;
  accent?: string;
  icon?: React.ComponentType<{ size?: number; strokeWidth?: number; color?: string }>;
  hint?: string;
}) {
  return (
    <Card>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
        {IconCmp && (
          <span
            style={{
              width: 22,
              height: 22,
              borderRadius: 6,
              background: `${accent ?? "var(--nd-orange)"}1a`,
              color: accent ?? "var(--nd-orange)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <IconCmp size={12} strokeWidth={2} />
          </span>
        )}
        <span
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: "var(--wf-ink-3)",
            textTransform: "uppercase",
            letterSpacing: "0.06em",
          }}
        >
          {label}
        </span>
      </div>
      <div
        className="wf-display"
        style={{
          fontSize: 28,
          fontWeight: 700,
          letterSpacing: "-0.02em",
          color: accent ?? "var(--wf-ink)",
          lineHeight: 1.05,
        }}
      >
        {value}
      </div>
      {subtitle && (
        <div style={{ fontSize: 12, color: "var(--wf-ink-2)", marginTop: 6 }}>
          {subtitle}
        </div>
      )}
      {hint && (
        <div style={{ fontSize: 11, color: "var(--wf-ink-3)", marginTop: 6, lineHeight: 1.5 }}>
          {hint}
        </div>
      )}
    </Card>
  );
}

function DailyChart({ data }: { data: CostReport["dailySeries"] }) {
  if (data.length === 0) {
    return (
      <div
        style={{
          padding: 28,
          textAlign: "center",
          color: "var(--wf-ink-3)",
          fontSize: 13,
        }}
      >
        sem dados ainda no período
      </div>
    );
  }

  const max = Math.max(...data.map((d) => d.costBrl), 0.01);
  const W = 720;
  const H = 140;
  const PAD = 12;
  const barW = (W - PAD * 2) / data.length;

  return (
    <div style={{ width: "100%", overflow: "auto" }}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="xMinYMid meet"
        style={{ width: "100%", maxWidth: W, height: H }}
      >
        {[0.25, 0.5, 0.75, 1].map((p, i) => (
          <line
            key={i}
            x1={PAD}
            x2={W - PAD}
            y1={H - PAD - (H - PAD * 2) * p}
            y2={H - PAD - (H - PAD * 2) * p}
            stroke="var(--wf-ink-5)"
            strokeDasharray="2 4"
            strokeWidth={1}
          />
        ))}
        {data.map((d, i) => {
          const h = (d.costBrl / max) * (H - PAD * 2);
          const x = PAD + i * barW + 2;
          const y = H - PAD - h;
          return (
            <g key={d.day}>
              <rect
                x={x}
                y={y}
                width={Math.max(0, barW - 4)}
                height={Math.max(2, h)}
                rx={3}
                fill="var(--nd-orange)"
                opacity={0.85}
              />
              <title>
                {new Date(d.day).toLocaleDateString("pt-BR")}: {fmtCredits(d.costBrl)} ({d.events} {d.events === 1 ? "evento" : "eventos"})
              </title>
            </g>
          );
        })}
      </svg>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          fontSize: 10,
          color: "var(--wf-ink-3)",
          padding: "4px 12px 0",
        }}
      >
        <span>
          {new Date(data[0]!.day).toLocaleDateString("pt-BR", { day: "2-digit", month: "short" })}
        </span>
        <span>
          {new Date(data[data.length - 1]!.day).toLocaleDateString("pt-BR", { day: "2-digit", month: "short" })}
        </span>
      </div>
    </div>
  );
}

interface BarRow {
  key: string;
  label: string;
  sublabel?: string | null;
  cost: number;
  metaText?: string;
  accent?: string;
  href?: string;
}

function BarList({ rows, total }: { rows: BarRow[]; total: number }) {
  if (rows.length === 0) {
    return (
      <div
        style={{
          padding: "20px 4px",
          textAlign: "center",
          color: "var(--wf-ink-3)",
          fontSize: 13,
        }}
      >
        sem dados
      </div>
    );
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {rows.map((r) => {
        const pct = total > 0 ? (r.cost / total) * 100 : 0;
        const accent = r.accent ?? "var(--nd-orange)";
        const inner = (
          <>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                fontSize: 13,
                marginBottom: 4,
              }}
            >
              <span
                style={{
                  fontWeight: 600,
                  color: "var(--wf-ink)",
                  flex: 1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {r.label}
              </span>
              <span style={{ fontWeight: 600, color: "var(--wf-ink)" }}>
                {fmtCredits(r.cost)}
              </span>
              <span
                style={{
                  fontSize: 11,
                  color: "var(--wf-ink-3)",
                  minWidth: 42,
                  textAlign: "right",
                }}
              >
                {pct.toFixed(1)}%
              </span>
            </div>
            <div
              style={{
                height: 6,
                background: "var(--wf-ink-5)",
                borderRadius: 999,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  width: `${pct}%`,
                  height: "100%",
                  background: accent,
                  borderRadius: 999,
                  transition: "width 280ms ease",
                }}
              />
            </div>
            {(r.sublabel || r.metaText) && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: 11,
                  color: "var(--wf-ink-3)",
                  marginTop: 4,
                }}
              >
                <span>{r.sublabel ?? ""}</span>
                <span>{r.metaText ?? ""}</span>
              </div>
            )}
          </>
        );
        if (r.href) {
          return (
            <Link
              key={r.key}
              to={r.href}
              style={{
                display: "block",
                textDecoration: "none",
                color: "inherit",
                padding: "8px 10px",
                borderRadius: 8,
                transition: "background 120ms",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,112,0,0.04)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
            >
              {inner}
            </Link>
          );
        }
        return (
          <div key={r.key} style={{ padding: "8px 10px" }}>
            {inner}
          </div>
        );
      })}
    </div>
  );
}

export default function Consumo() {
  const companyId = useCompanyId();
  const navigate = useNavigate();
  const [period, setPeriod] = useState<CostReportPeriod>("mtd");

  const report = useQuery({
    queryKey: ["companies", companyId, "cost-report", period],
    queryFn: () => costsApi.report(companyId!, period),
    enabled: !!companyId,
    refetchInterval: 30_000,
  });

  // Limite mensal da conta — o usuário precisa saber o teto, não só o gasto.
  // (1 ND Credit = 1 centavo de R$, então cents → créditos é 1:1.)
  const dashboard = useQuery({
    queryKey: ["companies", companyId, "dashboard"],
    queryFn: () => companiesApi.dashboard(companyId!),
    enabled: !!companyId,
    refetchInterval: 30_000,
  });

  const data = report.data;

  const totalCostBrl = data?.totals.costBrl ?? 0;
  const totalEvents = data?.totals.events ?? 0;

  // Custo médio por execução em ND Credits — vira KPI didático
  const avgCostBrlPerEvent = totalEvents > 0 ? totalCostBrl / totalEvents : 0;

  // Economia gerada pelo reaproveitamento de contexto (cache) — exibida em
  // R$/credits e em linguagem de produto (nada de "tokens"/"cache" pro usuário).
  const cachedSavingsBrl = data
    ? (data.totals.cachedInputTokens / 1_000_000) * 0.9 * 5.5
    : 0;

  // KPI "Agente mais ativo" — quem da equipe de IA mais produziu no período.
  const topAgent = useMemo(() => {
    if (!data || data.byAgent.length === 0) return null;
    const top = [...data.byAgent].sort((a, b) => b.costBrl - a.costBrl)[0]!;
    const share = totalCostBrl > 0 ? (top.costBrl / totalCostBrl) * 100 : 0;
    return { name: top.agentName ?? "Equipe", share, events: top.events };
  }, [data, totalCostBrl]);

  // Quebra por categoria (text/image/video/audio) renderizada na seção
  // "Por categoria" mais abaixo — não precisa duplicar nos KPI cards.

  return (
    <div>
      <div
        style={{
          background: "var(--wf-paper)",
          borderBottom: "1px solid var(--wf-ink-5)",
          padding: "24px 32px 20px",
        }}
      >
        <div style={{ maxWidth: 1180, margin: "0 auto" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              marginBottom: 6,
              fontSize: 11,
              fontWeight: 600,
              color: "var(--wf-ink-3)",
              textTransform: "uppercase",
              letterSpacing: "0.06em",
            }}
          >
            <Zap size={13} strokeWidth={1.75} color="var(--nd-orange)" />
            Consumo
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 14,
              flexWrap: "wrap",
            }}
          >
            <h1
              className="wf-display"
              style={{
                fontSize: 26,
                fontWeight: 700,
                letterSpacing: "-0.02em",
                margin: 0,
                lineHeight: 1.1,
              }}
            >
              Acompanhe o consumo da sua equipe de IA
            </h1>
            <div style={{ flex: 1 }} />
            <div
              style={{
                display: "flex",
                gap: 4,
                background: "var(--wf-ink-5)",
                borderRadius: 999,
                padding: 3,
              }}
            >
              {(
                [
                  { v: "mtd" as const, label: "Este mês" },
                  { v: "7d" as const, label: "Últimos 7 dias" },
                  { v: "30d" as const, label: "Últimos 30 dias" },
                ]
              ).map((opt) => (
                <button
                  key={opt.v}
                  type="button"
                  onClick={() => setPeriod(opt.v)}
                  style={{
                    border: "none",
                    cursor: "pointer",
                    padding: "5px 14px",
                    borderRadius: 999,
                    fontSize: 12,
                    fontWeight: 600,
                    background: period === opt.v ? "var(--wf-paper)" : "transparent",
                    color: period === opt.v ? "var(--wf-ink)" : "var(--wf-ink-3)",
                    boxShadow: period === opt.v ? "0 1px 3px rgba(0,0,0,0.08)" : "none",
                    fontFamily: "var(--wf-font-body)",
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          <p
            style={{
              fontSize: 13,
              color: "var(--wf-ink-2)",
              marginTop: 10,
              maxWidth: 720,
              lineHeight: 1.5,
            }}
          >
            Cada tarefa executada tem um custo em ND Credits. Aqui você acompanha quanto foi gasto por agente, por tarefa e por dia, e o que sua equipe de IA produziu no período.
          </p>
        </div>
      </div>

      <div
        style={{
          maxWidth: 1180,
          margin: "0 auto",
          padding: "24px 32px 64px",
          display: "flex",
          flexDirection: "column",
          gap: 20,
        }}
      >
        {/* Limite da conta — teto mensal de ND Credits. Fica no topo, sempre
            visível (independe do período do relatório, o limite é mensal). */}
        {dashboard.data && dashboard.data.costs.monthBudgetCents > 0 && (() => {
          const spent = dashboard.data.costs.monthSpendCents; // créditos = cents
          const budget = dashboard.data.costs.monthBudgetCents;
          const pct = Math.min(100, Math.max(0, (spent / budget) * 100));
          const over = spent > budget;
          const near = !over && pct >= 80;
          const barColor = over
            ? "var(--nd-orange-pun)"
            : near
              ? "#e08a2b"
              : "var(--nd-orange)";
          const fmt = (n: number) => Math.round(n).toLocaleString("pt-BR");
          return (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Card style={{ padding: "18px 22px" }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    flexWrap: "wrap",
                    marginBottom: 12,
                  }}
                >
                  <div
                    style={{
                      width: 34,
                      height: 34,
                      borderRadius: 10,
                      background: "rgba(255,112,0,0.12)",
                      color: "var(--nd-orange)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <Wallet size={17} strokeWidth={1.75} />
                  </div>
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: "var(--wf-ink-3)",
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                      }}
                    >
                      ND Credits
                    </span>
                  </div>
                  <div style={{ flex: 1 }} />
                  <div style={{ textAlign: "right" }}>
                    <span
                      className="wf-display"
                      style={{
                        fontSize: 22,
                        fontWeight: 700,
                        letterSpacing: "-0.02em",
                        color: over ? "var(--nd-orange-pun)" : "var(--wf-ink)",
                      }}
                    >
                      {fmt(spent)}
                    </span>
                    <span style={{ fontSize: 14, fontWeight: 600, color: "var(--wf-ink-3)" }}>
                      {" "}
                      de {fmt(budget)} créditos
                    </span>
                  </div>
                </div>
                <div
                  style={{
                    height: 10,
                    borderRadius: 999,
                    background: "var(--wf-ink-5)",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      height: "100%",
                      width: `${pct}%`,
                      background: barColor,
                      borderRadius: 999,
                      transition: "width 300ms",
                    }}
                  />
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginTop: 8,
                    fontSize: 12,
                  }}
                >
                  <span style={{ color: "var(--wf-ink-3)" }}>
                    {over
                      ? "Limite do mês atingido — fale com a gente pra liberar mais."
                      : near
                        ? "Você está chegando perto do limite do mês."
                        : `Restam ${fmt(budget - spent)} créditos neste mês.`}
                  </span>
                  <span style={{ fontWeight: 600, color: barColor }}>
                    {Math.round(pct)}% usado
                  </span>
                </div>
              </Card>
            </motion.div>
          );
        })()}

        {report.isLoading && (
          <div className="wf-hand-note" style={{ padding: 32, textAlign: "center" }}>
            carregando consumo…
          </div>
        )}

        {report.isError && (
          <div
            style={{
              padding: 16,
              background: "#fff0ee",
              border: "1px solid #ff5577",
              borderRadius: 10,
              color: "#cc3344",
              fontSize: 13,
            }}
          >
            erro ao carregar: {(report.error as Error).message}
          </div>
        )}

        {data && totalEvents === 0 && (
          <Card style={{ padding: 28, textAlign: "center" }}>
            <Sparkles
              size={28}
              strokeWidth={1.5}
              color="var(--nd-orange)"
              style={{ marginBottom: 10 }}
            />
            <div className="wf-display" style={{ fontSize: 18, marginBottom: 6 }}>
              Nada gasto ainda no período
            </div>
            <div
              style={{
                fontSize: 13,
                color: "var(--wf-ink-2)",
                lineHeight: 1.5,
                maxWidth: 460,
                margin: "0 auto",
              }}
            >
              Crie uma tarefa pra começar a popular esse painel. Cada execução registra aqui o que foi produzido e quanto custou.
            </div>
          </Card>
        )}

        {data && totalEvents > 0 && (
          <>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: 14,
              }}
            >
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                <KpiCard
                  label="ND Credits consumidos"
                  value={fmtCredits(totalCostBrl)}
                  subtitle={`${totalEvents.toLocaleString("pt-BR")} execuções`}
                  icon={Zap}
                  accent="var(--nd-orange)"
                />
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.05 }}
              >
                <KpiCard
                  label="Custo médio por tarefa"
                  value={fmtCredits(avgCostBrlPerEvent)}
                  subtitle={`${totalEvents.toLocaleString("pt-BR")} ${totalEvents === 1 ? "execução" : "execuções"} no período`}
                  icon={TrendingUp}
                  accent="var(--nd-blue)"
                />
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.1 }}
              >
                <KpiCard
                  label="Agente mais ativo"
                  value={topAgent?.name ?? "—"}
                  subtitle={
                    topAgent
                      ? `${fmtPct(topAgent.share)} do consumo · ${topAgent.events.toLocaleString("pt-BR")} ${topAgent.events === 1 ? "execução" : "execuções"}`
                      : "sem execuções no período"
                  }
                  icon={Bot}
                  accent="#5cb85c"
                />
              </motion.div>
            </div>

            {data.byKind && data.byKind.length > 0 && (
              <Card>
                <CardLabel>Por categoria</CardLabel>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
                    gap: 18,
                  }}
                >
                  {(["text", "image", "video", "audio"] as const).map((kind) => {
                    const entry = data.byKind?.find((k) => k.kind === kind);
                    const cost = entry?.costBrl ?? 0;
                    const events = entry?.events ?? 0;
                    const meta =
                      kind === "text"
                        ? { label: "Texto (LLM)", icon: FileText, accent: "var(--nd-blue)" }
                        : kind === "image"
                          ? { label: "Imagem", icon: ImageIcon, accent: "var(--nd-orange)" }
                          : kind === "video"
                            ? { label: "Vídeo", icon: Film, accent: "#5cb85c" }
                            : { label: "Áudio", icon: Bot, accent: "#999" };
                    const Icon = meta.icon;
                    const pct = totalCostBrl > 0 ? (cost / totalCostBrl) * 100 : 0;
                    return (
                      <div key={kind}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                            marginBottom: 4,
                            fontSize: 11,
                            fontWeight: 500,
                            color: "var(--wf-ink-3)",
                          }}
                        >
                          <Icon size={12} strokeWidth={1.75} color={meta.accent} />
                          {meta.label}
                        </div>
                        <div
                          className="wf-display"
                          style={{
                            fontSize: 22,
                            fontWeight: 700,
                            letterSpacing: "-0.015em",
                            color: cost > 0 ? "var(--wf-ink)" : "var(--wf-ink-3)",
                          }}
                        >
                          {fmtCredits(cost)}
                        </div>
                        <div
                          style={{
                            fontSize: 11,
                            color: "var(--wf-ink-3)",
                            marginTop: 2,
                          }}
                        >
                          {events} {events === 1 ? "execução" : "execuções"}
                          {pct > 0 && ` · ${pct.toFixed(0)}% do total`}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Card>
            )}

            <Card>
              <CardLabel>Gasto por dia</CardLabel>
              <DailyChart data={data.dailySeries} />
            </Card>

            {/* "Por modelo" (nomes de modelo de IA + tokens) era vazamento
                técnico pro usuário leigo — removido. O custo por agente conta a
                mesma história em linguagem de produto. */}
            <Card>
              <CardLabel>Por agente</CardLabel>
              <BarList
                total={totalCostBrl}
                rows={data.byAgent.map((a) => ({
                  key: a.agentId ?? "no-agent",
                  label: a.agentName ?? "(sem agente)",
                  sublabel: `${a.events.toLocaleString("pt-BR")} execuções`,
                  cost: a.costBrl,
                }))}
              />
            </Card>

            <Card>
              <CardLabel>Tarefas mais caras</CardLabel>
              <BarList
                total={totalCostBrl}
                rows={data.topIssues.map((t) => ({
                  key: t.issueId ?? "no-issue",
                  label: t.issueTitle ?? "(sem título)",
                  sublabel: t.issueIdentifier ?? undefined,
                  cost: t.costBrl,
                  metaText: `${t.events.toLocaleString("pt-BR")} execuções`,
                  href: t.issueId ? `/resultado/${t.issueId}` : undefined,
                }))}
              />
            </Card>

            <Card
              style={{
                background: "rgba(92,184,92,0.05)",
                borderColor: "rgba(92,184,92,0.3)",
              }}
            >
              <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                <span
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 8,
                    background: "rgba(92,184,92,0.15)",
                    color: "#5cb85c",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <TrendingUp size={16} strokeWidth={2} />
                </span>
                <div style={{ flex: 1 }}>
                  <div className="wf-display" style={{ fontSize: 15, marginBottom: 4 }}>
                    Você economizou cerca de {fmtCredits(cachedSavingsBrl)} no período
                  </div>
                  <div style={{ fontSize: 12, color: "var(--wf-ink-2)", lineHeight: 1.55 }}>
                    O Mohdus reaproveita o que já sabe sobre a sua empresa entre as tarefas, em vez de reprocessar tudo do zero a cada execução. Esse reaproveitamento gerou essa economia automática.
                  </div>
                </div>
              </div>
            </Card>

            {/* Aviso de "cache hit rate" removido: era papo interno de
                engenharia ("avise o time de engenharia") — não é acionável
                pelo usuário final. */}
          </>
        )}

        <div style={{ display: "flex", justifyContent: "center", gap: 14, marginTop: 8 }}>
          <button
            type="button"
            className="wf-btn wf-btn--ghost wf-btn--sm"
            onClick={() => navigate("/painel")}
            style={{ display: "inline-flex", alignItems: "center", gap: 5 }}
          >
            <Bot size={12} /> Voltar pro painel
          </button>
        </div>
      </div>
    </div>
  );
}
